import {
  AIONZ_DIALOG_DATA,
  AionzDialogModule,
  AionzDialogRef,
  CNPJ_MASK,
  HeaderBottomSheetComponent,
  SeedValidators,
  stripCnpj
} from '@aionzdev/design-system';
import { ChangeDetectionStrategy, Component, computed, inject, output, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { UserResponse } from '@aionzdev/modules';
import { PurchaseSupplier } from '../../../interfaces/purchase-supplier';

/** Item da solicitação que pode receber o fornecedor (mapa de cotação). */
export interface NewSupplierDialogItem {
  productId: string;
  code: string;
  product: string;
  branch?: string;
  /** Fornecedores que já venderam este item à empresa (histórico de compras). */
  previousSupplierIds?: string[];
  /**
   * CNPJs (só letras e números, maiúsculos: vale para o CNPJ alfanumérico) dos fornecedores que já estão neste item: não faz sentido convidar de novo.
   * Por CNPJ porque o id da lista de fornecedores das aceitas é o do vínculo, não o do usuário.
   */
  currentSupplierCnpjs?: string[];
}

/** Sem `items` (ou com um só), o fornecedor vai para a solicitação inteira, como antes. */
export interface NewSupplierDialogData {
  items?: NewSupplierDialogItem[];
}

export interface NewSupplierSaveEvent {
  supplier: PurchaseSupplier;
  /** null = todos os itens (rota da solicitação inteira); lista = só esses produtos. */
  productIds: string[] | null;
  /** Avança o "Adicionando 2 de 3…" do botão enquanto os itens são enviados um a um. */
  progress: (done: number, total: number) => void;
  done: (ok: boolean) => void;
}

@Component({
  selector: 'aionz-new-supplier-dialog',
  standalone: true,
  imports: [
    AionzDialogModule,
    HeaderBottomSheetComponent,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    NgxMaskDirective,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatCheckboxModule
  ],
  providers: [provideNgxMask()],
  templateUrl: './new-supplier-dialog.component.html',
  styleUrls: ['./new-supplier-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NewSupplierDialogComponent {
  public searchCNPJ = output<{
    cnpj: string;
    callback: (user: UserResponse | null) => void;
  }>();

  /**
   * Quem abre pode salvar antes de fechar (mapa de cotação): o dialog fica aberto com o botão em
   * "Adicionando…" e só fecha quando `done(true)`; com `done(false)` volta a ficar editável.
   * Sem isso (padrão), fecha na hora e devolve o fornecedor em afterClosed.
   */
  public savesBeforeClosing = false;
  public save = output<NewSupplierSaveEvent>();
  protected saving = signal(false);
  protected savingProgress = signal<{ done: number; total: number } | null>(null);

  // Escolha de itens: só quando o mapa passa os itens e há mais de um.
  private readonly data = inject<NewSupplierDialogData | null>(AIONZ_DIALOG_DATA, { optional: true });
  protected readonly items: NewSupplierDialogItem[] = this.data?.items ?? [];
  protected readonly canChooseItems = this.items.length > 1;
  protected readonly scope = signal<'all' | 'some'>('all');
  protected readonly selectedProductIds = signal<ReadonlySet<string>>(new Set());

  protected readonly scopeNote = computed(() => {
    if (this.alreadyInAllItems()) return 'Este fornecedor já está em todos os itens desta solicitação.';
    const available = this.availableItems().length;
    const present = this.presentProductIds().size;
    if (!this.canChooseItems || this.scope() === 'all') {
      if (!this.canChooseItems) return 'Ele será convidado para cotar todos os itens desta solicitação.';
      return present > 0
        ? `Ele será convidado para os ${available} ${available === 1 ? 'item' : 'itens'} em que ainda não está.`
        : `Ele será convidado para cotar todos os ${available} itens desta solicitação.`;
    }
    const n = this.selectedProductIds().size;
    if (n === 0) return 'Marque ao menos um item para convidar.';
    return `Ele será convidado para cotar ${n} ${n === 1 ? 'item' : 'itens'} desta solicitação.`;
  });

  /** Fornecedor encontrado pela busca de CNPJ (id na base), para cruzar com o histórico dos itens. */
  protected readonly foundSupplierId = signal<string | null>(null);
  /** CNPJ completo já buscado (só dígitos), para saber em quais itens ele já está. */
  protected readonly lookedUpCnpj = signal<string | null>(null);

  /** Itens desta solicitação que o fornecedor encontrado já forneceu à empresa. */
  protected readonly suppliedProductIds = computed(() => {
    const id = this.foundSupplierId();
    if (!id) return new Set<string>();
    return new Set(this.items.filter(item => item.previousSupplierIds?.includes(id)).map(item => item.productId));
  });

  /** Itens em que o fornecedor encontrado já está: aparecem travados e ficam fora do envio. */
  protected readonly presentProductIds = computed(() => {
    const cnpj = this.lookedUpCnpj();
    if (!cnpj) return new Set<string>();
    return new Set(this.items.filter(item => item.currentSupplierCnpjs?.includes(cnpj)).map(item => item.productId));
  });

  /** Itens que ainda podem receber o fornecedor. */
  protected readonly availableItems = computed(() =>
    this.items.filter(item => !this.presentProductIds().has(item.productId))
  );

  /** Já está em todos os itens: nada a adicionar. */
  protected readonly alreadyInAllItems = computed(
    () => this.items.length > 0 && this.availableItems().length === 0
  );

  /** Atalho: escolhe só os itens que ele já forneceu (e em que ainda não está). */
  protected selectSuppliedItems(): void {
    this.scope.set('some');
    this.selectedProductIds.set(
      new Set([...this.suppliedProductIds()].filter(id => !this.presentProductIds().has(id)))
    );
  }

  protected readonly missingItems = computed(
    () => this.alreadyInAllItems() || (this.scope() === 'some' && this.selectedProductIds().size === 0)
  );

  protected toggleItem(productId: string, checked: boolean): void {
    this.selectedProductIds.update(current => {
      const next = new Set(current);
      if (checked) next.add(productId);
      else next.delete(productId);
      return next;
    });
  }

  protected selectAllItems(): void {
    this.selectedProductIds.set(new Set(this.availableItems().map(item => item.productId)));
  }

  protected clearItems(): void {
    this.selectedProductIds.set(new Set());
  }

  protected isLoading = signal(false);
  /** Resultado da busca por CNPJ, mostrado abaixo do campo. */
  protected cnpjLookup = signal<'idle' | 'searching' | 'found' | 'notFound'>('idle');
  private fb = inject(FormBuilder);
  private dialogRef = inject(AionzDialogRef<NewSupplierDialogComponent, PurchaseSupplier>);

  protected readonly masks = {
    cnpj: CNPJ_MASK,
    phone: '(00) 0000-0000||(00) 0 0000-0000||+00 (00) 0000-0000||+00 (00) 0 0000-0000'
  };

  protected form: FormGroup = this.fb.group({
    name: ['', Validators.required],
    cnpj: ['', [Validators.required, SeedValidators.cnpj()]],
    stateRegistration: [''],
    phone: ['', [Validators.required, SeedValidators.phone()]],
    email: ['', [Validators.required, SeedValidators.email]]
  });

  constructor() {
    this.form.controls['cnpj'].valueChanges
      .pipe(distinctUntilChanged(), debounceTime(500))
      .subscribe(value => {
        const upper = (value ?? '').toUpperCase();
        if (upper !== value) {
          this.form.controls['cnpj'].setValue(upper, { emitEvent: false });
        }
        // CNPJ mudou: o resultado da busca anterior não vale mais.
        if (!this.isLoading()) {
          this.cnpjLookup.set('idle');
          this.foundSupplierId.set(null);
          this.lookedUpCnpj.set(null);
        }
        if (this.form.controls['cnpj'].valid && upper.length === 18 && !this.isLoading()) {
          this.handleSearchCNPJ(upper);
        }
      });
  }

  private handleSearchCNPJ(cnpj: string): void {
    this.isLoading.set(true);
    this.cnpjLookup.set('searching');
    this.form.disable();
    const clearCnpj = stripCnpj(cnpj);

    this.searchCNPJ.emit({
      cnpj: clearCnpj,
      callback: (user: UserResponse | null) => {
        this.isLoading.set(false);
        this.form.enable();
        this.cnpjLookup.set(user ? 'found' : 'notFound');
        this.foundSupplierId.set(user?.id ? String(user.id) : null);
        this.lookedUpCnpj.set(clearCnpj.replace(/[^0-9A-Z]/gi, '').toUpperCase());
        // Itens em que ele já está não podem continuar marcados.
        this.selectedProductIds.update(current => new Set([...current].filter(id => !this.presentProductIds().has(id))));

        if (user) {
          let phone = user.phone || '';
          if (phone && !phone.startsWith('55')) {
            phone = '55' + phone;
          }

          this.form.patchValue(
            {
              name: user.name,
              email: user.email,
              phone: phone,
              stateRegistration: user.stateRegistration || ''
            },
            { emitEvent: false }
          );
          this.form.markAllAsTouched();
          Object.values(this.form.controls).forEach(control => control.markAsDirty());
        }
      }
    });
  }

  protected close(): void {
    // Enquanto salva, fechar deixaria a criação sem retorno para a tela.
    if (this.saving()) return;
    this.dialogRef.close();
  }

  private removeSpecialCharacters(value: string): string {
    return value.replace(/[^\d]/g, '');
  }

  protected submit(): void {
    if (this.form.valid && !this.saving()) {
      const formValue = this.form.value;
      const supplier: PurchaseSupplier = {
        ...formValue,
        cnpj: stripCnpj(formValue.cnpj),
        stateRegistration: formValue.stateRegistration || undefined,
        phone: this.removeSpecialCharacters(formValue.phone),
        fantasyName: formValue.name,
        isVerified: false
      };
      if (!this.savesBeforeClosing) {
        this.dialogRef.close(supplier);
        return;
      }
      if (this.alreadyInAllItems()) return;
      const onlySome = this.canChooseItems && this.scope() === 'some';
      // Já está em parte dos itens: "todos" vira a lista dos que faltam (a rota da solicitação
      // inteira recusaria por ele já estar na oferta).
      const productIds = onlySome
        ? [...this.selectedProductIds()].filter(id => !this.presentProductIds().has(id))
        : this.presentProductIds().size > 0
          ? this.availableItems().map(item => item.productId)
          : null;
      if (productIds && productIds.length === 0) return;
      this.saving.set(true);
      this.savingProgress.set(null);
      this.form.disable({ emitEvent: false });
      this.save.emit({
        supplier,
        productIds,
        progress: (done, total) => this.savingProgress.set({ done, total }),
        done: ok => {
          if (ok) {
            this.dialogRef.close(supplier);
            return;
          }
          this.saving.set(false);
          this.savingProgress.set(null);
          this.form.enable({ emitEvent: false });
        }
      });
    }
  }

  protected getErrorMessage(controlName: string): string {
    const control = this.form.get(controlName);
    if (!control?.errors || !control.touched) return '';

    if (control.errors['required']) {
      return 'Campo obrigatório';
    }

    switch (controlName) {
      case 'cnpj':
        if (control.errors['cnpjInvalid']) {
          return 'CNPJ inválido';
        }
        break;
      case 'phone':
        if (control.errors['mask'] || control.errors['phone']) {
          return 'Telefone inválido';
        }
        break;
      case 'email':
        if (control.errors['email']) {
          return 'E-mail inválido';
        }
        break;
    }

    return '';
  }
}
